/**
 * useMeetingStream — React hook that subscribes to a meeting's WebSocket
 * stream via `streamMeeting` from ./api. Handles automatic reconnection
 * with capped exponential backoff, exposes connection state, the most
 * recent 200 server events, and the last error string.
 *
 * Consumer-supplied callbacks are stored in a ref so callers can pass
 * inline handlers without forcing the hook to re-subscribe.
 */

import { useEffect, useRef, useState } from 'react';
import { streamMeeting, type ServerEvent } from './api';

export interface MeetingStreamOptions {
  onTurnToken?: (ev: ServerEvent) => void;
  onTurnEnd?: (ev: ServerEvent) => void;
  onMeetingState?: (ev: ServerEvent) => void;
  onAgendaAdvance?: (ev: ServerEvent) => void;
  onError?: (ev: ServerEvent) => void;
}

export interface MeetingStreamResult {
  connected: boolean;
  events: ServerEvent[];
  lastError: string | null;
}

const MAX_EVENTS = 200;
const BACKOFF_STEPS_MS = [1000, 2000, 4000, 8000];

export function useMeetingStream(
  meetingId: string | null,
  opts?: MeetingStreamOptions,
): MeetingStreamResult {
  const [connected, setConnected] = useState(false);
  const [events, setEvents] = useState<ServerEvent[]>([]);
  const [lastError, setLastError] = useState<string | null>(null);

  // Stash latest callbacks in a ref so consumers can pass inline handlers
  // without our effect re-subscribing on every render.
  const optsRef = useRef<MeetingStreamOptions | undefined>(opts);
  optsRef.current = opts;

  // Tracks whether the latest close was triggered by cleanup/unmount/id-change
  // so we don't auto-reconnect in those cases.
  const closedByUser = useRef<boolean>(false);

  useEffect(() => {
    if (meetingId == null) {
      // Hook does nothing when no meeting is selected.
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffIdx = 0;
    let cancelled = false;

    const handleEvent = (ev: ServerEvent) => {
      // A successful event means the connection is healthy — reset backoff.
      backoffIdx = 0;
      setConnected(true);
      setLastError(null);
      setEvents((prev) => {
        const next = prev.length >= MAX_EVENTS ? prev.slice(-MAX_EVENTS + 1) : prev.slice();
        next.push(ev);
        return next;
      });

      const o = optsRef.current;
      if (!o) return;
      switch (ev.type) {
        case 'turn.token':
          o.onTurnToken?.(ev);
          break;
        case 'turn.end':
          o.onTurnEnd?.(ev);
          break;
        case 'meeting.state':
          o.onMeetingState?.(ev);
          break;
        case 'agenda.advance':
          o.onAgendaAdvance?.(ev);
          break;
        case 'system.error':
          setLastError(
            typeof ev.payload === 'string'
              ? ev.payload
              : ev.payload?.message ?? 'stream error',
          );
          o.onError?.(ev);
          break;
        default:
          break;
      }
    };

    const handleClose = () => {
      setConnected(false);
      if (cancelled) return;
      if (closedByUser.current) {
        // Intentional close (cleanup / id-change). Don't reconnect.
        closedByUser.current = false;
        return;
      }
      // Unexpected drop — schedule a reconnect with capped backoff.
      const delay = BACKOFF_STEPS_MS[Math.min(backoffIdx, BACKOFF_STEPS_MS.length - 1)];
      backoffIdx += 1;
      setLastError(`disconnected, retrying in ${delay}ms`);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (!cancelled) connect();
      }, delay);
    };

    const connect = () => {
      try {
        unsubscribe = streamMeeting(meetingId, handleEvent, handleClose);
      } catch (err) {
        setConnected(false);
        setLastError(err instanceof Error ? err.message : String(err));
        const delay = BACKOFF_STEPS_MS[Math.min(backoffIdx, BACKOFF_STEPS_MS.length - 1)];
        backoffIdx += 1;
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          if (!cancelled) connect();
        }, delay);
      }
    };

    connect();

    return () => {
      cancelled = true;
      closedByUser.current = true;
      if (reconnectTimer != null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      setConnected(false);
    };
  }, [meetingId]);

  return { connected, events, lastError };
}
